<template>
  <div class="project-list">

    <!-- 状态筛选标签 -->
    <div class="filter-tabs">
      <button 
        v-for="tab in filterTabs" 
        :key="tab.value"
        :class="['filter-tab', { active: activeFilter === tab.value }]"
        @click="activeFilter = tab.value"
      >{{ tab.label }}</button>
    </div>

    <!-- 项目卡片网格 -->
    <div class="project-cards">
      <div 
        v-for="project in filteredProjects" 
        :key="project.id" 
        class="project-card"
        @click="goToProject(project)"
      >
        <!-- 缩略图区域 -->
        <div class="card-cover" :style="getCoverStyle(project)">
          <span :class="['status-badge', getStatusClass(project.status)]">
            {{ project.status === 1 ? '进行中' : '已完成' }}
          </span>
        </div>

        <!-- 卡片内容 -->
        <div class="card-body">
          <div class="card-header">
            <h3 class="project-name">{{ project.name }}</h3>
            <button class="more-btn" @click.stop>
              <i class="el-icon-more" style="transform: rotate(90deg)"></i>
            </button>
          </div>
          <p class="project-code">ID: {{ project.code || project.id }}</p>

          <!-- 元信息 -->
          <div class="meta-row">
            <div class="meta-item">
              <i class="el-icon-document"></i>
              <span>{{ project.form_count || 0 }} 份问卷</span>
            </div>
            <div class="meta-item" v-if="project.end_time">
              <i class="el-icon-time"></i>
              <span>{{ formatDeadline(project.end_time) }}</span>
            </div>
            <div class="meta-item" v-else-if="project.customer_name">
              <i class="el-icon-user"></i>
              <span>{{ project.customer_name }}</span>
            </div>
          </div>

          <!-- 进度条 -->
          <div class="progress-section">
            <div class="progress-info">
              <span>进度 {{ project.totalApproved || 0 }}/{{ project.totalExpected || 0 }}</span>
              <span>{{ getProgress(project) }}%</span>
            </div>
            <div class="progress-bar">
              <div 
                class="progress-fill" 
                :class="{ completed: project.status !== 1 }"
                :style="{ width: getProgress(project) + '%' }"
              ></div>
            </div>
          </div>

          <!-- 底部 -->
          <div class="card-footer">
            <div class="member-avatars">
              <template v-if="project.members && project.members.length">
                <span 
                  v-for="(m, idx) in project.members.slice(0, 3)" 
                  :key="m.userId" 
                  class="avatar-circle"
                  :style="{ background: avatarColors[idx % avatarColors.length] }"
                  :title="m.name || m.username"
                >{{ getInitial(m) }}</span>
                <span class="avatar-count" v-if="project.members.length > 3">
                  +{{ project.members.length - 3 }}
                </span>
              </template>
              <template v-else>
                <span class="avatar-circle" style="background: #94a3b8">
                  <i class="el-icon-user" style="font-size:11px"></i>
                </span>
              </template>
            </div>
            <span class="view-link">{{ project.status === 1 ? '查看详情' : '查看结果' }}</span>
          </div>
        </div>
      </div>

      <!-- 新建项目卡片（仅管理员/全国级别用户可见） -->
      <div class="project-card add-card" @click="showCreateDialog" v-if="canCreateProject">
        <div class="add-icon-circle">
          <i class="el-icon-plus"></i>
        </div>
        <p class="add-title">创建新项目</p>
        <p class="add-desc">点击这里快速启动一个新的项目管理工作流</p>
      </div>
    </div>

    <!-- 新建/编辑项目对话框 -->
    <el-dialog 
      :visible.sync="dialogVisible" 
      width="600px"
      append-to-body
      custom-class="create-project-dialog"
      :show-close="false"
    >
      <!-- 自定义 Header -->
      <div class="cpd-header">
        <div class="cpd-header-left">
          <span class="cpd-icon"><i class="el-icon-folder-add"></i></span>
          <div>
            <h3 class="cpd-title">{{ editingProject ? '编辑项目' : '创建新项目' }}</h3>
            <p class="cpd-subtitle">填写详细信息以初始化您的工作区</p>
          </div>
        </div>
        <i class="el-icon-close cpd-close" @click="dialogVisible = false"></i>
      </div>

      <!-- Body -->
      <div class="cpd-body">
        <el-form ref="form" :model="formData" :rules="rules" label-position="top" class="cpd-form">
          <div class="cpd-row">
            <el-form-item label="项目名称" prop="name" class="cpd-col">
              <el-input v-model="formData.name" placeholder="例如：2026年度营销计划" />
            </el-form-item>
            <el-form-item label="项目编号" prop="code" class="cpd-col">
              <el-input v-model="formData.code" placeholder="PRJ-2026-001" />
            </el-form-item>
          </div>

          <el-form-item label="项目周期">
            <el-date-picker
              v-model="formData.timeRange"
              type="daterange"
              range-separator="至"
              start-placeholder="开始日期"
              end-placeholder="结束日期"
              value-format="yyyy-MM-dd"
              style="width: 100%"
              prefix-icon="el-icon-date"
            />
          </el-form-item>

          <el-form-item label="项目描述">
            <el-input 
              v-model="formData.description" 
              type="textarea" 
              :rows="4" 
              placeholder="请输入项目的目标、范围及关键成果..." 
            />
          </el-form-item>

          <el-form-item label="项目状态">
            <el-radio-group v-model="formData.status">
              <el-radio :label="1">进行中</el-radio>
              <el-radio :label="2">已完成</el-radio>
            </el-radio-group>
          </el-form-item>
        </el-form>

        <div class="cpd-tip">
          <i class="el-icon-info"></i>
          <span>提交后，您可以继续在项目面板中添加团队成员并分配初始任务。</span>
        </div>
      </div>

      <!-- Footer -->
      <div class="cpd-footer">
        <el-button @click="dialogVisible = false" class="cpd-cancel-btn">取消</el-button>
        <el-button type="primary" :loading="saving" @click="handleSave" class="cpd-save-btn">
          保存项目 <i v-if="!saving" class="el-icon-check"></i>
        </el-button>
      </div>
    </el-dialog>
  </div>
</template>

<script>
import { getProjectList, addProject, updateProject } from '@/app/index/api/project/manager'
import defaultCover from '@/app/index/assets/images/project_cover_default.png'

// 用项目名称生成稳定的封面颜色
const coverColors = [
  'linear-gradient(135deg, #667eea 0%, #764ba2 100%)',
  'linear-gradient(135deg, #f093fb 0%, #f5576c 100%)',
  'linear-gradient(135deg, #4facfe 0%, #00f2fe 100%)',
  'linear-gradient(135deg, #43e97b 0%, #38f9d7 100%)',
  'linear-gradient(135deg, #fa709a 0%, #fee140 100%)',
  'linear-gradient(135deg, #a18cd1 0%, #fbc2eb 100%)',
  'linear-gradient(135deg, #fccb90 0%, #d57eeb 100%)',
  'linear-gradient(135deg, #e0c3fc 0%, #8ec5fc 100%)'
]

export default {
  name: 'ProjectList',
  data() {
    return {
      searchName: '',
      activeFilter: 'all',
      dialogVisible: false,
      editingProject: null,
      saving: false,
      projects: [],
      avatarColors: ['#1476ff', '#e6a23c', '#f56c6c', '#67c23a', '#909399', '#e040fb'],
      filterTabs: [
        { label: '全部项目', value: 'all' },
        { label: '进行中', value: 'active' },
        { label: '已完成', value: 'done' },
        { label: '待开始', value: 'pending' }
      ],
      formData: {
        name: '',
        code: '',
        customer_name: '',
        timeRange: [],
        description: '',
        status: 1
      },
      rules: {
        name: [{ required: true, message: '请输入项目名称', trigger: 'blur' }]
      }
    }
  },
  computed: {
    filteredProjects() {
      let list = this.projects
      if (this.activeFilter === 'active') {
        list = list.filter(p => p.status === 1)
      } else if (this.activeFilter === 'done') {
        list = list.filter(p => p.status !== 1)
      }
      return list
    },
    // 是否有创建项目权限：只有研究员（项目管理）和管理员可新建
    canCreateProject() {
      const userInfo = (this.$store && this.$store.getters && this.$store.getters.userInfo) || {}
      const sysRole = (userInfo.roles && userInfo.roles.name) || userInfo.role_name || userInfo.roleName || ''
      const isAdmin = userInfo.username === 'admin' || userInfo.name === 'admin'
      return isAdmin || sysRole === '项目管理' || sysRole === '管理员' || sysRole === '研究'
    }
  },
  mounted() {
    this.loadProjects()
    // 监听 header 中的搜索和新建事件
    this.$root.$on('project-list-search', (keyword) => {
      this.searchName = keyword
      this.handleSearch()
    })
    this.$root.$on('project-list-create', () => {
      this.showCreateDialog()
    })
  },
  beforeDestroy() {
    this.$root.$off('project-list-search')
    this.$root.$off('project-list-create')
  },
  methods: {
    async loadProjects() {
      try {
        const res = await getProjectList({
          page: 1,
          pageSize: 100,
          keyword: this.searchName
        })
        const records = res.data?.data?.records || res.data?.records || []
        this.projects = records
      } catch (e) {
        const records = e?.data?.data?.records || e?.data?.records
        if (records && records.length > 0) {
          this.projects = records
        } else {
          console.error('加载项目列表失败', e)
        }
      }
    },
    goToProject(project) {
      // 根据用户有权限的菜单的第一项决定默认进入页面
      const surveyRoutes = this.$store.state.surveyRoutes || []
      const projectMenu = surveyRoutes.find(r => r.uri === '/project' || r.uri === '/')
      let firstPath = 'overview'
      if (projectMenu && projectMenu.children && projectMenu.children.length > 0) {
        const firstTab = projectMenu.children.find(item => {
          return item.is_menu === '1' && item.uri && item.uri.includes(':projectId')
        })
        if (firstTab) {
          const pathMatch = firstTab.uri.match(/\/project\/:projectId\/(.+)/)
          if (pathMatch) firstPath = pathMatch[1]
        }
      }
      this.$router.push(`/project/${project.id}/${firstPath}`)
    },
    getCoverStyle(project) {
      return {
        backgroundImage: `url(${defaultCover})`,
        backgroundSize: 'cover',
        backgroundPosition: 'center'
      }
    },
    getStatusClass(status) {
      return status === 1 ? 'status-active' : 'status-done'
    },
    getProgress(project) {
      if (project.progress !== undefined && project.progress !== null) return project.progress
      if (project.status !== 1) return 100
      return 0
    },
    formatDeadline(dateStr) {
      if (!dateStr) return ''
      const end = new Date(dateStr)
      const now = new Date()
      const diffDays = Math.ceil((end - now) / (1000 * 60 * 60 * 24))
      if (diffDays < 0) return '已过期'
      if (diffDays === 0) return '今天截止'
      if (diffDays <= 7) return `${diffDays}天后截止`
      const m = String(end.getMonth() + 1).padStart(2, '0')
      const d = String(end.getDate()).padStart(2, '0')
      return `${m}-${d} 结束`
    },
    getInitial(member) {
      const name = member.name || member.username || ''
      return name.substring(0, 1) || '?'
    },
    showCreateDialog() {
      this.editingProject = null
      this.formData = { name: '', code: '', customer_name: '', timeRange: [], description: '', status: 1 }
      this.dialogVisible = true
    },
    editProject(project) {
      this.editingProject = project
      const timeRange = []
      if (project.start_time || project.startTime) {
        timeRange[0] = project.start_time || project.startTime
      }
      if (project.end_time || project.endTime) {
        timeRange[1] = project.end_time || project.endTime
      }
      this.formData = {
        ...project,
        code: project.code || project.Code || '',
        customer_name: project.customer_name || project.customerName || '',
        timeRange: timeRange.length === 2 ? timeRange : []
      }
      this.dialogVisible = true
    },
    async handleSave() {
      try {
        await this.$refs.form.validate()
        this.saving = true
        
        const submitData = {
          ...this.formData,
          start_time: this.formData.timeRange && this.formData.timeRange[0] ? this.formData.timeRange[0] : null,
          end_time: this.formData.timeRange && this.formData.timeRange[1] ? this.formData.timeRange[1] : null
        }
        delete submitData.timeRange
        
        if (this.editingProject) {
            await updateProject(submitData)
        } else {
            await addProject(submitData)
        }
        this.$message.success(this.editingProject ? '项目更新成功' : '项目创建成功')
        this.dialogVisible = false
        this.loadProjects()
      } catch (e) {
        console.error(e)
      } finally {
        this.saving = false
      }
    },
    handleSearch() {
      this.loadProjects()
    },
    formatDate(dateStr) {
      if (!dateStr) return ''
      const d = new Date(dateStr)
      const y = d.getFullYear()
      const m = String(d.getMonth() + 1).padStart(2, '0')
      const day = String(d.getDate()).padStart(2, '0')
      return `${y}-${m}-${day}`
    }
  }
}
</script>

<style scoped>
.project-list {
  padding: 24px 32px;
  background: #f8fafc;
  min-height: 100%;
}

/* ========== 工具栏 ========== */
.toolbar {
  display: flex;
  align-items: center;
  justify-content: space-between;
  margin-bottom: 24px;
}

.search-input {
  width: 280px;
}

.search-input >>> .el-input__inner {
  background: #f1f5f9;
  border: none;
  border-radius: 8px;
  height: 36px;
  font-size: 13px;
}

.search-input >>> .el-input__inner:focus {
  box-shadow: 0 0 0 2px rgba(20, 118, 255, 0.15);
}

.create-btn {
  background: #1476ff !important;
  border: none !important;
  border-radius: 8px !important;
  font-size: 13px;
  font-weight: 600;
  padding: 0 16px;
  height: 36px;
}

.create-btn:hover {
  background: rgba(20, 118, 255, 0.9) !important;
}

/* ========== 筛选标签 ========== */
.filter-tabs {
  display: flex;
  align-items: center;
  gap: 10px;
  margin-bottom: 24px;
}

.filter-tab {
  padding: 6px 16px;
  border-radius: 999px;
  border: none;
  font-size: 13px;
  font-weight: 500;
  cursor: pointer;
  transition: all 0.2s;
  background: white;
  color: #64748b;
}

.filter-tab:hover {
  background: #f1f5f9;
}

.filter-tab.active {
  background: #1476ff;
  color: white;
  box-shadow: 0 2px 8px rgba(20, 118, 255, 0.25);
}

/* ========== 卡片网格 ========== */
.project-cards {
  display: grid;
  grid-template-columns: repeat(auto-fill, minmax(280px, 1fr));
  gap: 24px;
}

/* ========== 项目卡片 ========== */
.project-card {
  background: white;
  border-radius: 12px;
  border: 1px solid #e2e8f0;
  overflow: hidden;
  transition: all 0.3s ease;
  cursor: pointer;
}

.project-card:hover {
  box-shadow: 0 8px 24px rgba(0, 0, 0, 0.1);
}

/* 缩略图区域 */
.card-cover {
  height: 140px;
  position: relative;
  display: flex;
  align-items: center;
  justify-content: center;
  overflow: hidden;
}

.status-badge {
  position: absolute;
  top: 12px;
  left: 12px;
  padding: 3px 10px;
  border-radius: 4px;
  font-size: 10px;
  font-weight: 700;
  text-transform: uppercase;
  letter-spacing: 0.5px;
  color: white;
}

.status-active {
  background: #22c55e;
}

.status-done {
  background: #94a3b8;
}

/* 卡片内容 */
.card-body {
  padding: 20px;
}

.card-header {
  display: flex;
  justify-content: space-between;
  align-items: flex-start;
  margin-bottom: 4px;
}

.more-btn {
  background: none;
  border: none;
  padding: 4px;
  cursor: pointer;
  color: #94a3b8;
  font-size: 18px;
  line-height: 1;
  flex-shrink: 0;
  transition: color 0.15s;
}
.more-btn:hover { color: #1476ff; }

.project-name {
  margin: 0;
  font-size: 15px;
  font-weight: 700;
  color: #0f172a;
  overflow: hidden;
  text-overflow: ellipsis;
  white-space: nowrap;
}

.project-code {
  font-size: 12px;
  color: #94a3b8;
  margin: 0 0 14px;
}

/* 元信息 */
.meta-row {
  display: flex;
  align-items: center;
  gap: 16px;
  margin-bottom: 14px;
}

.meta-item {
  display: flex;
  align-items: center;
  gap: 4px;
  font-size: 12px;
  color: #64748b;
}

.meta-item i {
  font-size: 14px;
  color: #94a3b8;
}

/* 进度条 */
.progress-section {
  margin-bottom: 0;
}

.progress-info {
  display: flex;
  justify-content: space-between;
  font-size: 10px;
  font-weight: 500;
  color: #94a3b8;
  margin-bottom: 6px;
}

.progress-bar {
  width: 100%;
  height: 5px;
  background: #f1f5f9;
  border-radius: 999px;
  overflow: hidden;
}

.progress-fill {
  height: 100%;
  background: #1476ff;
  border-radius: 999px;
  transition: width 0.6s ease;
}

.progress-fill.completed {
  background: #cbd5e1;
}

/* 底部 */
.card-footer {
  display: flex;
  align-items: center;
  justify-content: space-between;
  margin-top: 18px;
  padding-top: 14px;
  border-top: 1px solid #f1f5f9;
}

.member-avatars {
  display: flex;
  align-items: center;
}

.avatar-circle {
  width: 24px;
  height: 24px;
  border-radius: 50%;
  border: 2px solid white;
  display: flex;
  align-items: center;
  justify-content: center;
  margin-right: -6px;
  color: white;
  font-size: 11px;
  font-weight: 600;
  flex-shrink: 0;
}

.avatar-count {
  width: 24px;
  height: 24px;
  border-radius: 50%;
  background: #f1f5f9;
  border: 2px solid white;
  display: flex;
  align-items: center;
  justify-content: center;
  font-size: 9px;
  font-weight: 700;
  color: #64748b;
}

.view-link {
  font-size: 13px;
  font-weight: 600;
  color: #1476ff;
}

.project-card:hover .view-link {
  text-decoration: underline;
}

/* ========== 新建项目卡片 ========== */
.add-card {
  display: flex;
  flex-direction: column;
  align-items: center;
  justify-content: center;
  border: 2px dashed #e2e8f0 !important;
  background: transparent !important;
  min-height: 300px;
  transition: all 0.3s;
}

.add-card:hover {
  border-color: rgba(20, 118, 255, 0.5) !important;
  background: rgba(241, 245, 249, 0.5) !important;
  box-shadow: none !important;
}

.add-icon-circle {
  width: 48px;
  height: 48px;
  border-radius: 50%;
  background: #f1f5f9;
  display: flex;
  align-items: center;
  justify-content: center;
  margin-bottom: 16px;
  transition: all 0.3s;
  color: #94a3b8;
}

.add-icon-circle i {
  font-size: 24px;
}

.add-card:hover .add-icon-circle {
  background: #1476ff;
  color: white;
}

.add-title {
  font-size: 15px;
  font-weight: 600;
  color: #0f172a;
  margin: 0 0 4px;
}

.add-desc {
  font-size: 12px;
  color: #94a3b8;
  margin: 0;
  text-align: center;
  max-width: 200px;
}
</style>

<!-- 创建项目弹窗样式（append-to-body 需要全局样式） -->
<style lang="scss">
.create-project-dialog {
  border-radius: 16px !important;
  overflow: hidden;
  box-shadow: 0 20px 60px rgba(0, 0, 0, 0.15) !important;

  .el-dialog__header { display: none !important; }
  .el-dialog__body { padding: 0 !important; }
  .el-dialog__footer { display: none !important; }
}

/* Header */
.cpd-header {
  display: flex;
  justify-content: space-between;
  align-items: flex-start;
  padding: 28px 32px 0;
}
.cpd-header-left {
  display: flex;
  align-items: center;
  gap: 14px;
}
.cpd-icon {
  width: 44px; height: 44px;
  border-radius: 10px;
  background: #eef4ff;
  display: flex; align-items: center; justify-content: center;
  flex-shrink: 0;
  i { font-size: 22px; color: #1476ff; }
}
.cpd-title {
  font-size: 20px; font-weight: 700; color: #0f172a; margin: 0;
}
.cpd-subtitle {
  font-size: 13px; color: #94a3b8; margin: 4px 0 0;
}
.cpd-close {
  font-size: 20px; color: #94a3b8; cursor: pointer; transition: color .15s; margin-top: 4px;
  &:hover { color: #334155; }
}

/* Body */
.cpd-body {
  padding: 24px 32px 8px;
}
.cpd-form {
  .el-form-item__label {
    font-weight: 600 !important;
    color: #0f172a !important;
    font-size: 14px !important;
    padding-bottom: 6px !important;
  }
  .el-input__inner, .el-textarea__inner {
    border-radius: 10px !important;
    border-color: #e2e8f0 !important;
    font-size: 14px !important;
    &:focus { border-color: #1476ff !important; }
  }
  .el-textarea__inner {
    padding: 12px 14px !important;
  }
  .el-input__inner {
    height: 42px !important;
    line-height: 42px !important;
  }
}

/* 必填星号 */
.cpd-form .el-form-item.is-required > .el-form-item__label::before {
  color: #f56c6c !important;
}

/* 双列 */
.cpd-row {
  display: flex; gap: 16px;
  .cpd-col { flex: 1; min-width: 0; }
}

/* 提示条 */
.cpd-tip {
  display: flex;
  align-items: center;
  gap: 10px;
  background: #f0f7ff;
  border-radius: 10px;
  padding: 12px 16px;
  margin-top: 4px;

  i { font-size: 16px; color: #1476ff; flex-shrink: 0; }
  span { font-size: 13px; color: #64748b; line-height: 1.5; }
}

/* Footer */
.cpd-footer {
  display: flex;
  justify-content: flex-end;
  align-items: center;
  gap: 12px;
  padding: 16px 32px 28px;
}
.cpd-cancel-btn {
  border: none !important;
  background: transparent !important;
  color: #64748b !important;
  font-size: 14px !important;
  font-weight: 500 !important;
  &:hover { color: #334155 !important; }
}
.cpd-save-btn {
  border-radius: 8px !important;
  background: #1476ff !important;
  border-color: #1476ff !important;
  font-size: 14px !important;
  font-weight: 600 !important;
  padding: 10px 28px !important;
  i { margin-left: 4px; }
  &:hover {
    background: #0d5fdb !important;
    border-color: #0d5fdb !important;
  }
}
</style>
